import React from "react";
import {
    AlarmClock,
    ArrowLeft,
    BookOpen,
    Clock,
} from "lucide-react";
import {
    useNavigate,
    useParams,
} from "react-router-dom";

const purpleActionBtn = `
    inline-flex items-center justify-center gap-2
    rounded-2xl
    border border-[#ffd166]/45
    bg-[#2b1548]/70
    px-4 py-2
    text-sm font-semibold text-[#fff3c4]
    shadow-[0_0_18px_rgba(168,85,247,0.22)]
    transition-all duration-200
    hover:border-[#ffd166]
    hover:bg-[#3b1d62]
    hover:text-[#ffd166]
`;

const safelyReadStorage = (key, fallback = []) => {
    try {
        const storedValue = localStorage.getItem(key);

        if (!storedValue) {
            return fallback;
        }

        return JSON.parse(storedValue);
    } catch (error) {
        console.error(
            `Unable to read ${key}:`,
            error
        );

        return fallback;
    }
};

const formatLocalDate = (date) => {
    const year = date.getFullYear();

    const month = String(
        date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
        date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const timeToMinutes = (time) => {
    if (
        !time ||
        !String(time).includes(":")
    ) {
        return null;
    }

    const [hours, minutes] = String(time)
        .split(":")
        .map(Number);

    if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes)
    ) {
        return null;
    }

    return hours * 60 + minutes;
};

const getEventDate = (event) =>
    event?.date_start ||
    event?.date ||
    event?.scheduled_date ||
    event?.due_date ||
    "";

const getEventStartTime = (event) =>
    event?.scheduled_start_time ||
    event?.start_time ||
    "";

const getEventEndTime = (event) =>
    event?.scheduled_end_time ||
    event?.end_time ||
    "";

export default function ChildBreakRoom() {
    const navigate = useNavigate();
    const { childId } = useParams();
const [subjects, setSubjects] =
    React.useState(() =>
        safelyReadStorage(
            "shadowGroveSubjects",
            []
        )
    );

const [events, setEvents] =
    React.useState(() =>
        safelyReadStorage(
            "shadowGroveEvents",
            []
        )
    );

const [now, setNow] =
    React.useState(() => new Date());
const [alarmsEnabled, setAlarmsEnabled] =
    React.useState(false);

const [lastAlarmKey, setLastAlarmKey] =
    React.useState("");
React.useEffect(() => {
    const refreshBreakRoomData = () => {
        setSubjects(
            safelyReadStorage(
                "shadowGroveSubjects",
                []
            )
        );

        setEvents(
            safelyReadStorage(
                "shadowGroveEvents",
                []
            )
        );
    };

    window.addEventListener(
        "storage",
        refreshBreakRoomData
    );

    window.addEventListener(
        "shadowGroveSubjectsUpdated",
        refreshBreakRoomData
    );

    window.addEventListener(
        "shadowGroveEventsUpdated",
        refreshBreakRoomData
    );

    return () => {
        window.removeEventListener(
            "storage",
            refreshBreakRoomData
        );

        window.removeEventListener(
            "shadowGroveSubjectsUpdated",
            refreshBreakRoomData
        );

        window.removeEventListener(
            "shadowGroveEventsUpdated",
            refreshBreakRoomData
        );
    };
}, []);

const childSubjects = React.useMemo(
    () =>
        subjects.filter(
            (subject) =>
                String(subject.child_id) ===
                String(childId)
        ),
    [subjects, childId]
);

const subjectsById = React.useMemo(() => {
    const map = {};

    childSubjects.forEach((subject) => {
        const currentSubjectId =
            subject.subject_id ??
            subject.id;

        if (!currentSubjectId) return;

        map[String(currentSubjectId)] =
            subject;
    });

    return map;
}, [childSubjects]);

const todayDateString =
    formatLocalDate(now);

const currentMinutes =
    now.getHours() * 60 +
    now.getMinutes() +
    now.getSeconds() / 60;

const todayScheduledClasses =
    React.useMemo(() => {
        return events
            .filter((event) => {
                const eventChildId =
                    event?.child_id ??
                    event?.childId;

                const subjectIdValue =
                    event?.subject_id ??
                    event?.subjectId;

                const startTime =
                    getEventStartTime(event);

                return (
                    String(eventChildId) ===
                        String(childId) &&
                    getEventDate(event) ===
                        todayDateString &&
                    Boolean(subjectIdValue) &&
                    Boolean(startTime) &&
                    !event?.archived
                );
            })
            .map((event) => {
                const currentSubjectId =
                    String(
                        event.subject_id ??
                            event.subjectId
                    );

                return {
                    ...event,
                    subjectId:
                        currentSubjectId,
                    subject:
                        subjectsById[
                            currentSubjectId
                        ] || null,
                    startMinutes:
                        timeToMinutes(
                            getEventStartTime(
                                event
                            )
                        ),
                    endMinutes:
                        timeToMinutes(
                            getEventEndTime(
                                event
                            )
                        ),
                };
            })
            .filter(
                (event) =>
                    event.startMinutes !== null
            )
            .sort(
                (a, b) =>
                    a.startMinutes -
                    b.startMinutes
            );
    }, [
        events,
        childId,
        todayDateString,
        subjectsById,
    ]);

const nextScheduledClass =
    React.useMemo(() => {
        return (
            todayScheduledClasses.find(
                (event) =>
                    event.startMinutes >
                    currentMinutes
            ) || null
        );
    }, [
        todayScheduledClasses,
        currentMinutes,
    ]);

const secondsUntilNextClass =
    nextScheduledClass
        ? Math.max(
              0,
              Math.round(
                  (nextScheduledClass.startMinutes -
                      currentMinutes) *
                      60
              )
          )
        : 0;
React.useEffect(() => {
    if (
        !alarmsEnabled ||
        !nextScheduledClass
    ) {
        return;
    }

    const nextClassKey =
        `${nextScheduledClass.subjectId}-${getEventStartTime(
            nextScheduledClass
        )}`;

    let alarmLabel = "";

    if (
        secondsUntilNextClass <= 300 &&
        secondsUntilNextClass > 60
    ) {
        alarmLabel = "5-minute";
    } else if (
        secondsUntilNextClass <= 60 &&
        secondsUntilNextClass > 0
    ) {
        alarmLabel = "1-minute";
    } else if (
        secondsUntilNextClass === 0
    ) {
        alarmLabel = "starting";
    }

    if (!alarmLabel) {
        return;
    }

    const alarmKey =
        `${nextClassKey}-${alarmLabel}`;

    if (lastAlarmKey === alarmKey) {
        return;
    }

    setLastAlarmKey(alarmKey);

    const audioContext =
        new (
            window.AudioContext ||
            window.webkitAudioContext
        )();

    const oscillator =
        audioContext.createOscillator();

    const gain =
        audioContext.createGain();

    oscillator.connect(gain);
    gain.connect(
        audioContext.destination
    );

    oscillator.frequency.value =
        alarmLabel === "starting"
            ? 880
            : alarmLabel === "1-minute"
            ? 660
            : 520;

    gain.gain.value = 0.08;

    oscillator.start();

    window.setTimeout(() => {
        oscillator.stop();
        audioContext.close();
    }, 650);
}, [
    alarmsEnabled,
    lastAlarmKey,
    nextScheduledClass,
    secondsUntilNextClass,
]);
React.useEffect(() => {
    if (
        !nextScheduledClass ||
        secondsUntilNextClass !== 0
    ) {
        return;
    }

    const transitionTimer =
        window.setTimeout(() => {
            navigate(
                `/child/classroom/${childId}/${nextScheduledClass.subjectId}`
            );
        }, 1500);

    return () => {
        window.clearTimeout(
            transitionTimer
        );
    };
}, [
    childId,
    navigate,
    nextScheduledClass,
    secondsUntilNextClass,
]);
const formatCountdown = (
    secondsValue
) => {
    const safeSeconds = Math.max(
        0,
        Math.floor(secondsValue)
    );

    const hours = Math.floor(
        safeSeconds / 3600
    );

    const minutes = Math.floor(
        (safeSeconds % 3600) / 60
    );

    const seconds =
        safeSeconds % 60;

    if (hours > 0) {
        return `${String(hours).padStart(
            2,
            "0"
        )}:${String(minutes).padStart(
            2,
            "0"
        )}:${String(seconds).padStart(
            2,
            "0"
        )}`;
    }

    return `${String(minutes).padStart(
        2,
        "0"
    )}:${String(seconds).padStart(
        2,
        "0"
    )}`;
};
    return (
        <div
            className="
                min-h-screen
                bg-[#05070d]
                text-white
                bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.12),transparent_40%)]
            "
        >
            <header className="border-b border-[#ffd166]/20 bg-black/60 backdrop-blur-sm">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
                    <button
                        type="button"
                        onClick={() =>
                            navigate(
                                `/child/dashboard/${childId}`
                            )
                        }
                        className={purpleActionBtn}
                    >
                        <ArrowLeft size={16} />
                        Dashboard
                    </button>

                    <h1 className="flex items-center gap-2 font-display text-2xl text-[#ffd166]">
                        <Clock size={22} />
                        Break Room
                    </h1>

                    <div className="w-[110px]" />
                </div>
            </header>

            <main className="mx-auto max-w-6xl space-y-6 px-6 py-10">
                <section className="rounded-[32px] border border-[#ffd166]/40 bg-black p-8 text-center shadow-[0_0_35px_rgba(255,209,102,0.12)]">
                    <AlarmClock
                        size={44}
                        className="mx-auto text-[#ffd166]"
                    />

                    <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#d8b4fe]">
                        Next Class Begins In
                    </p>

                    <div className="mt-3 font-display text-7xl text-[#ffd166]">
                        {nextScheduledClass
                            ? formatCountdown(
                                secondsUntilNextClass
                            )
                            : "--:--"}
                    </div>

                    <p className="mt-4 text-sm text-[#b0b8d6]">
                        {nextScheduledClass
                            ? `${
                                nextScheduledClass.subject
                                    ?.name ||
                                "Next Class"
                            } starts at ${getEventStartTime(
                                nextScheduledClass
                            )}.`
                            : "No more scheduled classes today."}
                    </p>
                    <button
                        type="button"
                        onClick={() =>
                            setAlarmsEnabled((current) => !current)
                        }
                        className={`${purpleActionBtn} mt-5`}
                    >
                        <AlarmClock size={16} />

                        {alarmsEnabled
                            ? "Class Alarms Enabled"
                            : "Enable Class Alarms"}
                    </button>
                                    </section>

                <section className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-[32px] border border-[#ffd166]/30 bg-black p-6">
                        <h2 className="flex items-center gap-2 font-display text-2xl text-[#ffd166]">
                            <BookOpen size={20} />
                            Up Next
                        </h2>

                        {nextScheduledClass ? (
                            <div className="mt-4 space-y-4">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                                        Next Class
                                    </p>

                                    <h3
                                        className="mt-1 font-display text-3xl"
                                        style={{
                                            color:
                                                nextScheduledClass.subject?.color ||
                                                "#ffd166",
                                        }}
                                    >
                                        {nextScheduledClass.subject?.name ||
                                            nextScheduledClass.title ||
                                            "Next Class"}
                                    </h3>

                                    <p className="mt-2 text-sm text-[#b0b8d6]">
                                        {getEventStartTime(
                                            nextScheduledClass
                                        )}
                                        {getEventEndTime(
                                            nextScheduledClass
                                        )
                                            ? ` - ${getEventEndTime(
                                                nextScheduledClass
                                            )}`
                                            : ""}
                                    </p>
                                </div>

                                {(nextScheduledClass.description ||
                                    nextScheduledClass.notes ||
                                    nextScheduledClass.instructions) && (
                                    <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                        <p className="text-xs uppercase tracking-[0.16em] text-[#ffd166]">
                                            Instructions
                                        </p>

                                        <p className="mt-2 text-sm leading-6 text-[#d6d9e6]">
                                            {nextScheduledClass.instructions ||
                                                nextScheduledClass.description ||
                                                nextScheduledClass.notes}
                                        </p>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/child/classroom/${childId}/${nextScheduledClass.subjectId}`
                                        )
                                    }
                                    className={purpleActionBtn}
                                >
                                    <BookOpen size={16} />
                                    Enter Class Now
                                </button>
                            </div>
                        ) : (
                            <p className="mt-3 text-sm text-[#b0b8d6]">
                                There are no more scheduled classes today.
                            </p>
                        )}
                    </div>

                    <div className="rounded-[32px] border border-[#ffd166]/30 bg-black p-6">
                        <h2 className="font-display text-2xl text-[#ffd166]">
                            Before Class
                        </h2>

                        {nextScheduledClass ? (
                            <div className="mt-4 space-y-4">
                                <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                    <p className="text-xs uppercase tracking-[0.16em] text-[#ffd166]">
                                        Parent Notes
                                    </p>

                                    <p className="mt-2 text-sm leading-6 text-[#d6d9e6]">
                                        {nextScheduledClass.parent_note ||
                                            nextScheduledClass.parent_notes ||
                                            nextScheduledClass.notes ||
                                            "No special notes for this class."}
                                    </p>
                                </div>

                                <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                    <p className="text-xs uppercase tracking-[0.16em] text-[#ffd166]">
                                        Materials Needed
                                    </p>

                                    {Array.isArray(
                                        nextScheduledClass.materials
                                    ) &&
                                    nextScheduledClass.materials.length > 0 ? (
                                        <div className="mt-3 space-y-2">
                                            {nextScheduledClass.materials.map(
                                                (material, index) => (
                                                    <div
                                                        key={
                                                            material.id ||
                                                            index
                                                        }
                                                        className="flex items-start gap-2 text-sm text-[#d6d9e6]"
                                                    >
                                                        <span className="mt-0.5 text-[#ffd166]">
                                                            •
                                                        </span>

                                                        <span>
                                                            {typeof material ===
                                                            "string"
                                                                ? material
                                                                : material.name ||
                                                                "Material"}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <p className="mt-2 text-sm text-[#b0b8d6]">
                                            No materials listed.
                                        </p>
                                    )}
                                </div>

                                <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-4">
                                    <p className="text-xs uppercase tracking-[0.16em] text-[#ffd166]">
                                        To-Do
                                    </p>

                                    <p className="mt-2 text-sm leading-6 text-[#d6d9e6]">
                                        {nextScheduledClass.todo ||
                                            nextScheduledClass.to_do ||
                                            nextScheduledClass.prep ||
                                            nextScheduledClass.preparation ||
                                            "Nothing extra to prepare."}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <p className="mt-3 text-sm text-[#b0b8d6]">
                                Nothing else is scheduled for today.
                            </p>
                        )}
                    </div>
                </section>
            <section className="rounded-[32px] border border-[#ffd166]/30 bg-black p-6 shadow-[0_0_30px_rgba(168,85,247,0.10)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                            Classroom Shortcuts
                        </p>

                        <h2 className="mt-1 font-display text-2xl text-[#ffd166]">
                            Your Classes
                        </h2>
                    </div>
                </div>

                {childSubjects.length > 0 ? (
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {childSubjects.map((subject) => {
                            const currentSubjectId =
                                subject.subject_id ??
                                subject.id;

                            const subjectColor =
                                subject.color ||
                                "#ffd166";

                            return (
                                <button
                                    key={currentSubjectId}
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/child/classroom/${childId}/${currentSubjectId}`
                                        )
                                    }
                                    className="rounded-2xl border bg-[#080b14] p-4 text-left transition-all duration-200 hover:-translate-y-0.5"
                                    style={{
                                        borderColor:
                                            subjectColor,
                                        boxShadow: `0 0 18px ${subjectColor}22`,
                                    }}
                                >
                                    <div className="flex items-center gap-3">
                                        <BookOpen
                                            size={18}
                                            style={{
                                                color:
                                                    subjectColor,
                                            }}
                                        />

                                        <span
                                            className="font-display text-lg"
                                            style={{
                                                color:
                                                    subjectColor,
                                            }}
                                        >
                                            {subject.name ||
                                                "Class"}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <p className="mt-4 text-sm text-[#b0b8d6]">
                        No classes are available yet.
                    </p>
                )}
</section>
            </main>
        </div>
    );
}